import { useCallback, useEffect, useState } from 'react';
import '../backlog/backlog.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { FormReportsMenu } from '../../components/FormReportsMenu';
import { useGridTextSearch } from './useGridTextSearch';
import { useNotify } from '../../components/notifications';
import { ReagentListShell } from './ReagentListShell';
import { GridSearch } from '../../components/GridSearch';
import { DateRangeSelect } from '../modules/DateRangeSelect';
import { fetchLltSearch } from './api';
import { REAGENT_RANGES, rangeCode, rangeDays, type LltSearchRow, type ReagentDateFilter } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Reagent Preparation → LLT Search (nav `rgp-lltsearch`).
// Port of legacy ALPACA `ReagentPreparation_ListView_LLTSearch`; spec §16.1.
//
// Read-only BY DESIGN, in legacy as well: the view sets AllowNew / AllowDelete
// false and hides the Edit action.  The screen exists to FIND an LLT and then
// print its report or label.  Those two toolbar actions need the report
// endpoint (the PDF renderer itself already exists — `LltReagentCoaShaper`) and
// arrive in a later wave; until then the grid's own search, per-column filters,
// sort and export are the working surface.
//
// Columns — the LIVE G5 grid (owner screenshot 2026-11-03): LLT · PrepType ·
// PrepName · Department · ExpirationDate · PreparedBy · DatePrepared (date only),
// sorted LLT DESC. Division is in the Model.xafml but NOT on the live screen, so
// it is not shown (the API still returns it).
// ─────────────────────────────────────────────────────────────────────────────

const COLUMNS: AppColumn<LltSearchRow>[] = [
  { field: 'llt',            header: 'LLT',            width: 120 },
  { field: 'prepType',       header: 'PrepType',       width: 110 },
  { field: 'prepName',       header: 'PrepName',       width: 220 },
  { field: 'department',     header: 'Department',     width: 120 },
  { field: 'expirationDate', header: 'ExpirationDate', width: 120, type: 'date' },
  { field: 'preparedBy',     header: 'PreparedBy',     width: 130 },
  { field: 'datePrepared',   header: 'DatePrepared',   width: 120, type: 'date' },
];

export default function LltSearchPage({ onHome }: { onHome?: () => void }) {
  const notify = useNotify();
  const [rows, setRows]       = useState<LltSearchRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs]   = useState<number | null>(null);
  const [dateFilter, setDateFilter] = useState<ReagentDateFilter | null>(null);
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  // Two boxes, as G5: the toolbar Search and the grid's own find panel
  // (XAF ShowFindPanel="True", "Enter text to search..."). A row must match both.
  const [search, setSearch] = useState('');
  const [find, setFind] = useState('');
  const shown = useGridTextSearch(useGridTextSearch(rows, COLUMNS, search, 'contains'), COLUMNS, find, 'contains');

  const load = useCallback(async (filter?: ReagentDateFilter) => {
    setLoading(true);
    const t0 = performance.now();
    try {
      const res = await fetchLltSearch(filter);
      setRows(res.rows);
      setDateFilter(res.dateFilter);
    } catch (e) {
      notify.error(e instanceof Error ? e.message : 'Failed to load LLT search.');
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { void load(); }, [load]);

  // No List / Detail switch: LLT Search has no detail view (legacy hides Edit, AllowNew/AllowDelete false).
  return (
    <ReagentListShell
      pageId="rgp-lltsearch" caption="LLT Search" auditFormName="Reagent Prep Log"
      loadMs={loadMs} loading={loading} onHome={onHome}
      // Same bar shape as the Reagent Prep Log (G5 order, 2026-11-03):
      // Reports ▾ · Refresh · Date Filter · Export to · Search.
      toolbar={<AppToolbar
        left={
          // Reports ▾ — lab-assigned to ReagentPreparation_ListView_LLTSearch.
          <FormReportsMenu
            reportsUrl="/api/v1/reagent-preps/reports?view=llt-search"
            pdfUrl="/api/v1/reagent-preps/report-pdf"
            keysParam="llts"
            // G5 keeps Reports clickable with nothing selected (bug list #9, 2026-11-03).
            enabledWithoutSelection
            selectedKeys={rows.filter((r) => selected.has(r.oid) && r.llt).map((r) => r.llt!)}
          />
        }
        onRefresh={() => load(dateFilter ?? undefined)}
        refreshing={loading}
        beforeExport={
          <DateRangeSelect
            variant="field" label="Date Filter" ranges={REAGENT_RANGES}
            value={dateFilter ? rangeDays(dateFilter) : null}
            onChange={(days) => { const c = rangeCode(days); setDateFilter(c); void load(c); }}
          />
        }
        // G5's split Export: the button exports CSV, its ▾ opens every format.
        exportData={{ filename: 'llt-search', ...gridExportMatrix(shown, COLUMNS), defaultCsv: true }}
        right={<GridSearch variant="toolbar" applyOnEnter value={search} onChange={setSearch} placeholder="Text to search..." title="Filter records by text" />}
      />}
    >
      <AppDataGrid<LltSearchRow>
                  selectAllTitle="Selection state of all rows on all pages"
        rows={shown}
        columns={COLUMNS}
        getRowId={(r) => r.oid}
        selectable
        selectedIds={selected}
        onSelectionChange={setSelected}
        // G5 (DevExpress select-by-row-click): clicking a row SELECTS it — that row
        // alone, ready for Reports ▾ — and opens nothing. The tick boxes still add
        // rows one at a time (owner request 2026-11-03).
        onRowClick={(r) => setSelected(new Set([r.oid]))}
        loading={loading}
        headerLeft={<GridSearch value={find} onChange={setFind} placeholder="Enter text to search..." style={{ padding: 0 }} />}
        highlightText={find}
        emptyText={search || find ? 'No results match your search.' : 'No lab lots found for this date range.'}
      />
    </ReagentListShell>
  );
}
